import { Col } from 'antd'
import { Formik } from 'formik'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import * as Yup from 'yup'
import { PerCardWithAvatar } from '../../components/custom_components/CardWithAvatar'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import PerFlexRow from '../../components/custom_components/FlexRow'
import MeetingBackground from '../../components/custom_components/Meeting/MeetingBackground'
import PerSpacer from '../../components/custom_components/Spacer'
import PerText from '../../components/custom_components/Text'
import { FormItem } from '../../components/formik-component'
import { useCollapse } from '../../routes/collapse-context'
import { FullMeetingFragment } from '../../types'
import { MeetingConfirmBtn } from '../appraisal/appraisal-flow/shared/MeetingStepButton'
import { AgendaOtherMeeting } from './AgendaOtherMeeting'
import { HintsOtherMeeting } from './HintsOtherMeeting'
import { OtherMeetingHeader } from './OtherMeetingHeader'
import { processOtherMeeting } from './OtherMeetings-helper'
import './OtherMeetings.less'
import RichTextBox from '../../components/custom_components/RichTextBox'

interface ManagerPrepareOtherMeetingProps {
  meeting: FullMeetingFragment
}
const ManagerPrepareOtherMeeting: FC<ManagerPrepareOtherMeetingProps> = ({ meeting }) => {
  const [t] = useTranslation()
  const history = useHistory()
  const collapsed = useCollapse()

  const redirect = () => {
    history.push('/meetings')
  }

  const employeePrepared = !!meeting.other && !!meeting.other.notesEmployee

  const renderName = (name, type: 'lastName' | 'firstName') => {
    if (type === 'firstName') {
      return name.chosenFirstName ? name.chosenFirstName : name.legalFirstName
    } else if (type === 'lastName') {
      return name.chosenLastName ? name.chosenLastName : name.legalLastName
    }
  }

  return (
    <ContentContainer collapsed={collapsed}>
      <Formik
        initialValues={{ textAreaValue: '' }}
        enableReinitialize={true}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          setSubmitting(true)
          await processOtherMeeting(t, meeting, values.textAreaValue, true)
          resetForm()
          setSubmitting(false)
          redirect()
        }}
        validateOnChange={true}
        validationSchema={Yup.object().shape({
          textAreaValue: Yup.string()
            .min(2, t('formError.commentLength'))
            .required(t('formError.required', { fieldName: t('otherMeetings.notes.headline') })),
        })}
      >
        {(props) => {
          const { isSubmitting, handleSubmit, isValid, initialValues, values } = props
          const isViolatingForm = !isValid || initialValues.textAreaValue === values.textAreaValue

          return (
            <>
              <OtherMeetingHeader meeting={meeting} isPreparation />
              <MeetingBackground withHeader>
                <PerText appearance="tile-heading">{t(`meetingTypes.${meeting.type}`)}</PerText>

                <PerSpacer mt="m">
                  <HintsOtherMeeting meeting={meeting} className="otherMeetings-collapse"></HintsOtherMeeting>
                </PerSpacer>

                {employeePrepared && (
                  <PerSpacer mt="xxxl">
                    <PerText appearance="tile-heading" mb="s">
                      {t('otherMeetings.feedback.headline')}
                    </PerText>
                    <PerFlexRow>
                      <Col span={12}>
                        <PerCardWithAvatar
                          nameType="firstName"
                          employeeId={meeting.employee.id}
                          firstName={renderName(meeting.employee, 'firstName')}
                          lastName={renderName(meeting.employee, 'lastName')}
                        >
                          <RichTextBox text={meeting.other ? meeting.other.notesEmployee || '' : ''} readonly />
                        </PerCardWithAvatar>
                      </Col>
                    </PerFlexRow>
                  </PerSpacer>
                )}

                <PerSpacer mt="xxxl">
                  <PerFlexRow>
                    <Col span={12}>
                      <PerText appearance="tile-heading" mb="s">
                        {t('otherMeetings.notes.headline')}
                      </PerText>
                    </Col>
                    {!!meeting.other && !!meeting.other.agenda && (
                      <Col span={12}>
                        <PerText appearance="tile-heading" mb="s">
                          {t('otherMeetings.agendaHeadline')}
                        </PerText>
                      </Col>
                    )}
                  </PerFlexRow>
                  <PerFlexRow>
                    <Col span={12}>
                      <FormItem name="textAreaValue">
                        <RichTextBox
                          placeholder={t('otherMeetings.notes.placeholder')}
                          data-e2e-other-meeting-notes
                          text={values.textAreaValue}
                          onChange={(text) => props.setFieldValue('textAreaValue', text)}
                        />
                      </FormItem>
                    </Col>
                    {!!meeting.other && !!meeting.other.agenda && (
                      <Col span={12}>
                        <AgendaOtherMeeting meeting={meeting} />
                      </Col>
                    )}
                  </PerFlexRow>
                </PerSpacer>
              </MeetingBackground>

              <PerFlexRow justify="end">
                <MeetingConfirmBtn
                  onMouseDown={() => handleSubmit()}
                  text={t('global.button.save')}
                  disabled={isViolatingForm || isSubmitting}
                />
              </PerFlexRow>
            </>
          )
        }}
      </Formik>
    </ContentContainer>
  )
}

export default ManagerPrepareOtherMeeting
